import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import Navbar, { BOTTOM_NAV_HEIGHT } from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import RequireAnthropicKey from './components/RequireAnthropicKey';
import OnboardingProgress from './components/OnboardingProgress';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Resume from './pages/Resume';
import Tailor from './pages/Tailor';
import Usage from './pages/Usage';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Home from './pages/Home';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AnthropicKeyProvider } from './context/AnthropicKeyContext';
import { getUserState } from './services/api';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0a2540', dark: '#0e2f52', light: '#1e3a5f' },  // Navy — brand
    secondary: { main: '#64748b' },  // Slate neutral
    success: { main: '#16a34a' },
    warning: { main: '#d97706' },
    error:   { main: '#dc2626' },
    info:    { main: '#0284c7' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#475569' },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { letterSpacing: '-0.3px', fontWeight: 600 },
    h5: { letterSpacing: '-0.2px', fontWeight: 600 },
    h6: { letterSpacing: '-0.2px', fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8 },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: 12,
          borderColor: '#e2e8f0',
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: '#94a3b8' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        sizeSmall: { height: 22, fontSize: 11 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backdropFilter: 'saturate(180%) blur(8px)' },
      },
    },
  },
});

// `/` is the smart entry surface (V1-11):
//
//   - Anonymous visitor    → public Landing
//   - Authenticated, no key       → /jobs (RequireAnthropicKey will show KeySetup)
//   - Authenticated, no resume    → /resume (upload page)
//   - Authenticated, no applications → /jobs (encourage first save)
//   - Authenticated, fully set up → render Home (mission control surface)
//
// We fetch user state via /api/user/state once on mount. While loading,
// show a centered spinner so neither the Landing nor the Home flashes.
function HomeOrLanding() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [stateLoading, setStateLoading] = useState(false);

  // Fetch onboarding state when auth resolves to a logged-in user.
  // Reset to null on logout so a future login refetches cleanly.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState(null);
      return;
    }
    let cancelled = false;
    setStateLoading(true);
    getUserState()
      .then((res) => {
        if (!cancelled) setState(res.data);
      })
      .catch(() => {
        // Network/auth error — fall through to /jobs as a safe default.
        // RequireAnthropicKey there will surface the KeySetup gate if
        // the user really has no key, which covers the most common cause.
        if (!cancelled) setState({ fallback: true });
      })
      .finally(() => {
        if (!cancelled) setStateLoading(false);
      });
    return () => { cancelled = true; };
  }, [authLoading, user]);

  // 1. Auth still resolving — render nothing (AuthProvider will trigger re-render)
  if (authLoading) return null;

  // 2. Anonymous → public Landing
  if (!user) return <Landing />;

  // 3. Logged in, fetching state — centered spinner (no flash of wrong content)
  if (stateLoading || !state) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 'calc(100vh - 64px)' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 4. State-aware redirects. Order matters — earlier check wins.
  if (state.fallback) return <Navigate to="/jobs" replace />;
  if (!state.has_key) return <Navigate to="/jobs" replace />;          // gate handles
  if (!state.has_resume) return <Navigate to="/resume" replace />;
  if (!state.has_applications) return <Navigate to="/jobs" replace />;

  // 5. Established user — show the Home surface (filled out by Nav 2)
  return <Home />;
}

// Shell that conditionally shows the Navbar — we hide it on /login and `/`
// (Landing) so they can use a clean full-bleed layout.
function AppShell() {
  const { user } = useAuth();
  // When the user is authenticated, the mobile bottom nav reserves a fixed
  // strip at the bottom of the viewport. Reserve matching padding-bottom on
  // <main> so the last element of every page stays visible.
  const mainPb = user ? {
    xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`,
    sm: 0,
  } : 0;

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      {user && <Navbar />}
      {/* OnboardingProgress self-hides for activated users; rendering it
          unconditionally for logged-in users is safe and keeps logic local. */}
      {user && <OnboardingProgress />}
      <Box component="main" flex={1} sx={{ bgcolor: 'background.default', pb: mainPb }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<HomeOrLanding />} />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          {/* Claude-powered routes — gated by RequireAnthropicKey. Without a
              connected Anthropic key, these render the KeySetup screen
              instead. Settings, Dashboard, and Usage are NOT gated so the
              user can still manage their account without a key. */}
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <RequireAnthropicKey><Jobs /></RequireAnthropicKey>
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume"
            element={
              <ProtectedRoute>
                <RequireAnthropicKey><Resume /></RequireAnthropicKey>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tailor/:applicationId"
            element={
              <ProtectedRoute>
                <RequireAnthropicKey><Tailor /></RequireAnthropicKey>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={<ProtectedRoute><Settings /></ProtectedRoute>}
          />
          {/* Internal — not linked from Navbar. Access directly via URL. */}
          <Route
            path="/usage"
            element={<ProtectedRoute><Usage /></ProtectedRoute>}
          />
          {/* Catch-all — any unknown path bounces to Landing/Jobs depending on
              auth state. Covers /terms and /privacy until V1-22 ships real
              pages, and any accidental typos. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <BrowserRouter>
          <AuthProvider>
            <AnthropicKeyProvider>
              <AppShell />
            </AnthropicKeyProvider>
          </AuthProvider>
        </BrowserRouter>
      </NotificationProvider>
    </ThemeProvider>
  );
}
